/**
 * Where a request actually goes.
 *
 * The shop has to keep selling when the internet drops, when the cloud is down,
 * or when a phone is simply out of range — so a call that can be served from
 * the device's own database is served from there rather than failing.
 *
 * The rule is deliberately narrow, because "fall back to local" is easy to get
 * dangerously wrong:
 *
 *   - A **network** failure means the server is unreachable. Fall back.
 *   - An **HTTP error** means the server was reached and said no. Do not fall
 *     back. A 403 is an answer, and answering it locally with a different
 *     result would turn a refusal into a way around itself.
 *
 * That distinction is the whole safety property here.
 */

import type { AuthenticatedUser } from '@bakery/shared';

import { localCommand, whenLocalDbReady } from '../data/local-db.js';
import { cachedUser } from './session-cache.js';
import { ApiError } from './api.js';

export type Reachability = 'online' | 'offline' | 'unknown';

let reachability: Reachability = 'unknown';
const listeners = new Set<(state: Reachability) => void>();

/** The signed-in user, needed to enforce permissions locally. */
let currentUser: AuthenticatedUser | null = null;

export function setTransportUser(user: AuthenticatedUser | null): void {
  currentUser = user;
}

export function reachabilityState(): Reachability {
  return reachability;
}

export function onReachabilityChange(listener: (state: Reachability) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function setReachability(next: Reachability): void {
  if (next === reachability) return;
  reachability = next;
  for (const listener of listeners) listener(next);
}

/**
 * Gateway statuses that mean "I could not reach the server", not "the server
 * said no".
 *
 * This matters more than it looks. The app is almost always behind a proxy —
 * Vite in development, nginx or a tunnel in production — and when the app
 * server is down the *proxy* answers. So `fetch` resolves normally with a 502
 * or 504, and a naive "an HTTP response means the server answered" rule would
 * refuse to fall back at exactly the moment falling back is the whole point.
 *
 * 500 is deliberately **not** in this list. A 500 is ambiguous — it is equally
 * a genuine bug in a server that is perfectly reachable — and quietly answering
 * those from the local database would hide real faults and let two devices
 * diverge. The dev proxy is configured to return 503 instead, so development
 * behaves the way production does.
 */
const GATEWAY_STATUSES = new Set([502, 503, 504]);

/**
 * Did this fail because the server could not be reached?
 *
 * `fetch` rejects with a TypeError for DNS failures, refused connections and
 * dropped wifi; a proxy in front of a dead server answers with a gateway status.
 * Anything else means the server itself replied, and its reply stands.
 */
function isUnreachable(error: unknown): boolean {
  if (error instanceof ApiError) return GATEWAY_STATUSES.has(error.status);

  const name = (error as Error)?.name;
  // TimeoutError is what AbortSignal.timeout produces; an address that simply
  // never answers is as unreachable as one that refuses.
  return error instanceof TypeError || name === 'AbortError' || name === 'TimeoutError';
}

export class OfflineUnavailableError extends Error {
  constructor(what: string) {
    super(
      `${what} needs a connection, and there is none right now. ` +
        `Selling, parking a sale and looking up a customer still work.`,
    );
    this.name = 'OfflineUnavailableError';
  }
}

export interface RouteOptions<T> {
  /** The network call. Always tried first when the server is thought reachable. */
  remote: () => Promise<T>;
  /** The equivalent local command, when the device can serve this itself. */
  local?: { name: string; args?: Record<string, unknown> };
  /** Shown when there is neither a connection nor a local equivalent. */
  label: string;
}

/**
 * Try the server; fall back to this device's own database when it is unreachable.
 *
 * Always tries the network first, even when the last call failed. A shop's wifi
 * comes back without announcing itself, and a register that stayed stubbornly
 * offline until someone restarted it would be its own kind of outage.
 */
export async function route<T>({ remote, local, label }: RouteOptions<T>): Promise<T> {
  try {
    const result = await remote();
    setReachability('online');
    return result;
  } catch (error) {
    if (!isUnreachable(error)) {
      // The server answered. Its answer stands, whatever it was.
      throw error;
    }

    setReachability('offline');
    if (!local) throw new OfflineUnavailableError(label);

    // Awaited, not checked: a reload starts the screens querying before the
    // worker has finished opening the database, and a flag read at that moment
    // is false for a database that is about to be perfectly usable.
    const state = await whenLocalDbReady();
    if (!state.ready) throw new OfflineUnavailableError(label);

    // The cached user covers the same startup gap on the React side, where the
    // effect that publishes the signed-in user has not necessarily run yet.
    const user = currentUser ?? cachedUser();
    if (!user) throw new OfflineUnavailableError(label);

    return localCommand<T>(local.name, local.args ?? {}, user);
  }
}

/**
 * Run something only the server can do, with a clear message when it is away.
 * Used for the operations that have no local equivalent by design.
 */
export async function remoteOnly<T>(label: string, call: () => Promise<T>): Promise<T> {
  try {
    const result = await call();
    setReachability('online');
    return result;
  } catch (error) {
    if (isUnreachable(error)) {
      setReachability('offline');
      throw new OfflineUnavailableError(label);
    }
    throw error;
  }
}
